import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  HiFaceSmile,
  HiFaceFrown,
  HiOutlineCheckCircle,
} from "react-icons/hi2";
import { BiMeh } from "react-icons/bi";
import { toast } from "react-toastify";
import LanguageSelector from "../../atoms/LanguageSelector";
import { useLmcUserView } from "../../../contexts/AuthContext";
import LoyaltyLoginModal from "./LoyaltyLoginModal";
import { useSubmitFeedback } from "../../../hooks/useFeedback";
import { CustomButton } from "../../atoms/CustomButton";

interface FeedbackFilterProps {
  googleReviewUrl?: string;
  entityIdentification: string;
}

type FeedbackLevel = "green" | "orange" | "red" | null;

const FeedbackFilter: React.FC<FeedbackFilterProps> = ({
  googleReviewUrl,
  entityIdentification,
}) => {
  const { t } = useTranslation("clientView");
  const navigate = useNavigate();
  const { processId } = useParams<{ processId: string }>();
  const {
    state: { auth },
  } = useLmcUserView();

  const [selectedLevel, setSelectedLevel] = useState<FeedbackLevel>(null);
  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [pendingSubmitAfterLogin, setPendingSubmitAfterLogin] = useState(false);

  const maxCharacters = 400;

  const userIdentification = auth?.user_identification;
  const isAuthenticated = auth?.isAuthenticated;

  const authToken = useMemo(() => {
    return {
      username: auth?.user_identification || "",
      password: auth?.access_token || "",
    };
  }, [auth?.user_identification, auth?.access_token]);

  const { mutateAsync: submitFeedbackMutation } = useSubmitFeedback();

  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  const handleLevelSelect = (level: FeedbackLevel) => {
    if (level === "green") {
      if (googleReviewUrl) {
        window.open(googleReviewUrl, "_blank", "noopener,noreferrer");
      }
      navigate(`/loyalty-program/${processId}`);
    } else {
      setSelectedLevel(level);
      setFeedback("");
    }
  };

  const handleFeedbackChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= maxCharacters) {
      setFeedback(value);
    }
  };

  const handleSubmit = () => {
    if (!feedback.trim()) {
      toast.error(t("feedback.errorEmpty"));
      return;
    }

    if (!isAuthenticated) {
      setShowLoginForm(true);
      return;
    }

    sendFeedback();
  };

  const sendFeedback = useCallback(async () => {
    if (userIdentification === undefined) return;

    setIsSubmitting(true);

    try {
      await submitFeedbackMutation({
        entity_identification: entityIdentification,
        user_identification: userIdentification || "",
        authToken: authToken,
        data: {
          level: selectedLevel || "",
          feedback: feedback.trim(),
        },
      });

      setShowThankYou(true);
    } catch (error) {
      console.error("Failed to save feedback");
      setShowThankYou(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    entityIdentification,
    userIdentification,
    authToken,
    selectedLevel,
    feedback,
    submitFeedbackMutation,
  ]);

  const handleBack = () => {
    navigate(`/loyalty-program/${processId}`);
  };

  const handleLoginSuccess = useCallback(() => {
    setShowLoginForm(false);
    setPendingSubmitAfterLogin(true);
  }, []);

  useEffect(() => {
    if (isAuthenticated && pendingSubmitAfterLogin) {
      setPendingSubmitAfterLogin(false);
      sendFeedback();
    }
  }, [isAuthenticated, pendingSubmitAfterLogin, sendFeedback]);

  useEffect(() => {
    if (showThankYou) {
      const timer = setTimeout(() => {
        navigate(`/loyalty-program/${processId}`);
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [showThankYou, navigate, processId]);

  const getFaceColor = (level: string) => {
    switch (level) {
      case "green":
        return "bg-green-500 hover:bg-green-600";
      case "orange":
        return "bg-amber-400 hover:bg-amber-500";
      case "red":
        return "bg-red-500 hover:bg-red-600";
      default:
        return "bg-gray-300";
    }
  };

  const getFaceIcon = (level: string) => {
    const iconClass = "text-white";
    const size = 60;
    switch (level) {
      case "green":
        return <HiFaceSmile className={iconClass} size={size} />;
      case "orange":
        return <BiMeh className={iconClass} size={size} />;
      case "red":
        return <HiFaceFrown className={iconClass} size={size} />;
      default:
        return null;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm overflow-y-auto py-4"
      translate="no"
    >
      <div className="relative w-full max-w-md mx-4 bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl shadow-lg overflow-hidden my-auto">
        <div className="p-6 text-center border-b border-gray-700">
          <h1 className="text-lg font-semibold text-white">
            {t("feedback.title")}
          </h1>
        </div>

        <div className="p-6 flex flex-col items-center">
          {showThankYou && (
            <div className="py-8 flex flex-col items-center text-center">
              <HiOutlineCheckCircle className="w-20 h-20 text-green-400 mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">
                {t("feedback.thankYouTitle")}
              </h3>
              <p className="text-gray-400">{t("feedback.thankYouMessage")}</p>
            </div>
          )}

          {!showThankYou && showLoginForm && (
            <LoyaltyLoginModal
              entityIdentification={entityIdentification}
              onLoginSuccess={handleLoginSuccess}
              onBack={() => setShowLoginForm(false)}
            />
          )}

          {!showThankYou && !showLoginForm && !selectedLevel ? (
            <>
              <div className="flex justify-center items-center gap-4 mb-4">
                {(["green", "orange", "red"] as const).map((level) => (
                  <button
                    key={level}
                    onClick={() => handleLevelSelect(level)}
                    className={`relative w-24 h-24 rounded-full ${getFaceColor(
                      level
                    )} shadow-lg transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center group`}
                    aria-label={`Select ${level} feedback`}
                  >
                    <span className="filter drop-shadow-lg group-hover:scale-110 transition-transform">
                      {getFaceIcon(level)}
                    </span>

                    <span className="absolute inset-0 rounded-full border-4 border-white/30 group-hover:scale-110 transition-transform" />
                  </button>
                ))}
              </div>
            </>
          ) : !showThankYou && !showLoginForm && selectedLevel ? (
            <>
              <div className="mb-4 w-full">
                <div className="mb-2">
                  <label className="text-white font-medium text-base">
                    {t("feedback.yourFeedback")}
                  </label>
                </div>

                <div className="relative">
                  <textarea
                    value={feedback}
                    onChange={handleFeedbackChange}
                    className="w-full h-32 p-2.5 md:p-2 text-base md:text-sm bg-gray-800/50 text-white rounded-md border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 resize-none outline-none transition-all placeholder:text-gray-500"
                    maxLength={maxCharacters}
                  />
                  <div className="absolute bottom-2 end-2 text-gray-500 text-xs">
                    {feedback.length}/{maxCharacters}
                  </div>
                </div>
              </div>

              <CustomButton
                onClick={handleSubmit}
                disabled={!feedback.trim() || isSubmitting}
                isLoading={isSubmitting}
                title={
                  isSubmitting
                    ? t("games.overlay.processing")
                    : t("feedback.submit")
                }
                variant="primary"
                titleClassName="font-bold"
              />

              <CustomButton
                variant="none"
                size="sm"
                onClick={handleBack}
                title={t("common.cancel")}
                className="mt-2 w-full text-gray-400 hover:text-white"
              />
            </>
          ) : null}

          {!showThankYou && !showLoginForm && !selectedLevel && (
            <CustomButton
              variant="none"
              size="sm"
              onClick={handleBack}
              title={t("games.overlay.skip")}
              className="mt-4 w-full text-gray-400 hover:text-white"
            />
          )}
        </div>

        <div className="absolute top-4 end-4">
          <LanguageSelector variant="compact" />
        </div>
      </div>
    </div>
  );
};

export default FeedbackFilter;
